import { Component, OnInit, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';

import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";

import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';

import { DateService } from "../../../../common/services/date.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { SubHeaderService } from '../../../../core/services/sub-header.service';
import { SubHeader } from '../../../../core/models/sub-header.model';
import { BondSearchCriteria, LetterOfCreditModel } from '../../../models/admin-reports.model';
import { AdminReportsService } from '../../../services/admin-reports.service';
import { OperationType } from "../../../../common/models/organization.model";

@Component({
    selector: 'letters-of-credit-report',
    templateUrl: './letters-of-credit-report.component.html',
    standalone: false
})

export class LetterOfCreditTrackingComponent implements OnInit, OnDestroy {

    public reportData: LetterOfCreditModel[];
    gridOptions: GridOptions;
	gridApi?: GridApi;
    subHeader: SubHeader = new SubHeader({
        title: "Letter of Credit Tracking",
        buttons: [{ name: "Run Report", method: () => this.generateReport() }, { name: "Download Report", method: () => this.downloadReport() }]
    });

    subscription: Subscription = new Subscription();

    constructor(
        private datePipe: DatePipe,
        private readonly dateService: DateService,
        private readonly formatService: GridFormatterService,
        private readonly financialService: AdminReportsService,
        public router: Router,
        public subHeaderService: SubHeaderService
	) {  }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader);
        let coldefs = this.createColumnDefinitions();
        this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(coldefs, false, true, true, true);
        this.gridOptions.headerHeight = 50;
		this.gridOptions.onGridReady = (params) => { 
            this.gridApi = params.api; 
            this.onGridReady(params);
        };
        this.generateReport();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    generateReport() {
        if (this.gridApi) {
            this.gridApi.setGridOption("loading", true);
        }

        const criteria = new BondSearchCriteria();
        criteria.isActive = true;

        this.subscription = this.financialService.getLettersOfCredit(criteria).pipe(
            tap((data: LetterOfCreditModel[]) => {
                this.reportData = data;
            })).subscribe({
                next: (success) => {
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("rowData", this.reportData);
                        this.gridApi.setGridOption("loading", false);
                        this.gridApi.sizeColumnsToFit();
                    }
                },
                error: (fail) => {
                    console.error(fail);
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("loading", false);
                    }
                }
            });
    }

    downloadReport() {
        const params = {
            skipHeader: false,
            allColumns: true,
            fileName: this.formatFileName(),
            sheetName: "Letter of Credit Tracking",
            exportMode: "xlsx"
        }
        this.formatService.exportDataToExcel(params.fileName, params.sheetName, this.gridApi);
    }

    formatFileName(): string {
        const now = new Date();
        const frmDt = this.datePipe.transform(now, "yyyy-MM-dd:hh:mm").toString();
        const fileName = `LetterOfCreditTracking_${frmDt}`;
        return fileName;
    }

    public createColumnDefinitions() {
        return [
            {
                headerClass: "wrapped",
                headerName: "Owner Name",
                headerTooltip: "Owner Name",
                field: "ownerName",
                tooltipField: "ownerName",
                filterParams: this.formatService.defaultTextFilterParams,
                filter: "agTextColumnFilter",
                minWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "RICS Owner Number",
                headerTooltip: "RICS Owner Number",
                field: "ricsOwnerNumber",
                tooltipField: 'ricsOwnerNumber',
                filterParams: this.formatService.defaultTextFilterParams,
                filter: "agTextColumnFilter",
                minWidth: 140
            },
            {
                headerClass: "wrapped",
                headerName: "RBDMS Owner Number",
                headerTooltip: "RBDMS Owner Number",
                field: "rbdmsOwnerNumber",
                tooltipField: 'rbdmsOwnerNumber',
                filterParams: this.formatService.defaultTextFilterParams,
                filter: "agTextColumnFilter",
                minWidth: 140
            },
            {
                headerClass: "wrapped",
                headerName: "Owner Type",
                headerTooltip: "Owner Type",
                tooltipValueGetter: this.formatOwnerType(),
                valueGetter: this.formatOwnerType(),
                minWidth: 110
            },
            {
                headerClass: "wrapped",
                headerName: "Bank Name",
                headerTooltip: "Bank Name",
                field: "bankName",
                tooltipField: "bankName",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 150
            },
            {
                headerClass: "wrapped",
                headerName: "Bank Address",
                headerTooltip: "Bank Address",
                field: "bankAddress",
                tooltipField: "bankAddress",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "Bank Contact Phone",
                headerTooltip: "Bank Contact Phone",
                field: "bankContactPhoneNumber",
                tooltipField: "bankContactPhoneNumber",
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "Bank Contact Email",
                headerTooltip: "Bank Contact Email",
                valueGetter: this.emailFormatter(),
                tooltipValueGetter: this.emailFormatter(),
                filter: "agTextColumnFilter",
                filterParams: this.formatService.defaultTextFilterParams,
                minWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "Expiration Date",
                headerTooltip: "Expiration Date",
                field: "expirationDate",
                tooltipValueGetter: this.formatService.formatDate("expirationDate"),
                valueGetter: this.formatService.formatDate("expirationDate"),
                comparator: dateComparator,
                filterParams: this.formatService.defaultDateFilterParams,
                filter: "agDateColumnFilter",
                minWidth: 100
            }
        ] as ColDef[];
    }

    formatOwnerType(): any {
        return (obj) => {
            let returnValue: string = "";
            if (obj.data != null && obj.data["ownerType"] != null) {
                let isBrineHaulerFunc = this.formatService.flagEnumFormatter("ownerType", OperationType, "BrineHauler", true);
                let isOperatorFunc = (this.formatService.flagEnumFormatter("ownerType", OperationType, "Operator", true));
                let isInjectionOperatorFunc = (this.formatService.flagEnumFormatter("ownerType", OperationType, "InjectionOperator", true));
                if (isBrineHaulerFunc(obj) == true) {
                    returnValue = "Brine Hauler";
                }
                if (isOperatorFunc(obj) == true || isInjectionOperatorFunc(obj) == true) {
                    if (returnValue.length > 0) returnValue += ", ";
                    returnValue += "Well Owner";
                }
            }
            if (returnValue.length === 0) returnValue = "N/A";
            return returnValue;
        }
    }


    onGridPageChanged(event: any) {
        this.formatService.gridReadySizeToFit(event);
    }

    onGridReady(event: any) {
        this.formatService.gridReadySizeToFit(event);
    }

    emailFormatter(): any {
        return (obj: any) => {
            return obj.data.bankContactEmail == null || obj.data.bankContactEmail == "" ? "N/A" : obj.data.bankContactEmail;
        };
    }
}
